import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { LogService } from '../../../../services/log-service';
import { ModalService } from 'ui-components/services/modal-service';
import { defaultExportFormat } from '../enums/default-export-format';
import { ExportOptions, ExportOptionsValues } from '../../export-records-modal/enums/export-options-enum';
import { ErrorModal } from '../../error-modal';
import { GenericErrorRetryContainer } from '../../../../containers/generic-error-retry-container';
import { getSavedExportFormats, deleteExportFormat } from '../api';
import { messages } from '../../export-records-modal/messages/general-export-messages';
import { ExportRecordsModal } from '..';

export const ExportRecordsModalContainer = ({
  exportTypeEnum,
  onClose,
  numberRecordsInView,
  numberRecordsSelected,
  onExport,
  onUpdateExportType,
  recordExportType,
  retryAction,
}) => {
  const [selectedExportFormatTypeId, setSelectedExportFormatTypeId] = useState(defaultExportFormat.extractId);
  const [primaryRecordsOrLineItems, setPrimaryRecordsOrLineItems] = useState(ExportOptions.EXPORT_PRIMARY_RECORDS);
  const [exportFormats, setExportFormats] = useState([]);
  const formatsForType = [defaultExportFormat, ...(exportFormats || []).filter((format) => format.extractType === exportTypeEnum)];
  const [shouldLoadFormats, setShouldLoadFormats] = useState(true);
  const mountedRef = useRef(true);

  const onFormatTypeChanged = useCallback(
    (optionSelected) => {
      setSelectedExportFormatTypeId(optionSelected);
    },
    [setSelectedExportFormatTypeId],
  );

  const onExportRecordTypeChanged = useCallback(
    (optionSelected) => {
      setPrimaryRecordsOrLineItems(optionSelected);
      onFormatTypeChanged(defaultExportFormat.extractId);
      onUpdateExportType(optionSelected);
    },
    [setPrimaryRecordsOrLineItems, onFormatTypeChanged, onUpdateExportType],
  );

  const updateExportFormats = useCallback(
    (savedFormats) => {
      setExportFormats([...savedFormats]);
    },
    [setExportFormats],
  );

  const onSaveExportFormat = useCallback(() => {
    setShouldLoadFormats(true);
  }, [setShouldLoadFormats]);

  const onDeleteExportFormat = useCallback(
    (formatId) => {
      deleteExportFormat(formatId)
        .then(() => {
          if (!mountedRef.current) {
            return;
          }
          setShouldLoadFormats(true);
          onFormatTypeChanged(defaultExportFormat.extractId);
        })
        .catch((error) => {
          LogService.logError(new Error('Error deleting export format', { cause: error }));
          ModalService.launchModal({
            component: ErrorModal,
            props: {
              title: <FormattedMessage {...messages.errorTitle} />,
              message: <FormattedMessage {...messages.deleteError} />,
            },
          });
        });
    },
    [onFormatTypeChanged],
  );

  useEffect(() => {
    if (shouldLoadFormats) {
      getSavedExportFormats()
        .then((data) => {
          if (!mountedRef.current) {
            return;
          }
          updateExportFormats(data);
          setShouldLoadFormats(false);
        })
        .catch((error) => {
          if (!mountedRef.current) {
            return;
          }
          LogService.logError(new Error('Error fetching saved export formats', { cause: error }));
          onClose();
          setShouldLoadFormats(false);
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              onClick: retryAction,
              title: <FormattedMessage {...messages.errorTitle} />,
              message: <FormattedMessage {...messages.retrieveSavedFormatsError} />,
            },
          });
        });
    }
  }, [shouldLoadFormats, updateExportFormats, retryAction, onClose]);

  useEffect(() => {
    return () => (mountedRef.current = false);
  }, []);

  return (
    <ExportRecordsModal
      exportFormats={formatsForType}
      onClose={onClose}
      onDeleteExportFormat={onDeleteExportFormat}
      onExport={onExport}
      onExportTypeChanged={onExportRecordTypeChanged}
      onFormatTypeChanged={onFormatTypeChanged}
      onSaveExportFormat={onSaveExportFormat}
      primaryRecordsOrLineItems={primaryRecordsOrLineItems}
      recordExportType={recordExportType}
      selectedCount={numberRecordsSelected}
      selectedExportFormatTypeId={selectedExportFormatTypeId}
      viewCount={numberRecordsInView}
    />
  );
};

ExportRecordsModalContainer.propTypes = {
  exportTypeEnum: PropTypes.oneOf(ExportOptionsValues).isRequired,
  recordExportType: PropTypes.oneOf(ExportOptionsValues).isRequired,
  onExport: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  retryAction: PropTypes.func.isRequired,
  onUpdateExportType: PropTypes.func.isRequired,
  numberRecordsInView: PropTypes.number.isRequired,
  numberRecordsSelected: PropTypes.number.isRequired,
};
